import { format as formatDateFns, parseISO } from "date-fns";

export type DateInput = Date | string | number;

function toDate(value: DateInput): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string" && value.trim()) {
    const d = parseISO(value);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/**
 * Format a date value using date-fns.
 * Defaults to a compact date pattern used across the admin UI.
 */
export function formatDate(value: DateInput, pattern = "yyyy-MM-dd"): string {
  const date = toDate(value);
  if (!date) return "";
  return formatDateFns(date, pattern);
}

/**
 * Format a date-time value (24h).
 */
export function formatDateTime(
  value: DateInput,
  pattern = "yyyy-MM-dd HH:mm",
): string {
  const date = toDate(value);
  if (!date) return "";
  return formatDateFns(date, pattern);
}

/**
 * Format a count / quantity with grouping separators. Counts are genuine
 * `number`s — money is a MoneyIdr string, so it goes through {@link formatCurrency}.
 * Default locale: id-ID (aligns with Payroll region). Rejects non-finite → "".
 */
export function formatNumber(
  value: number | string | null | undefined,
  locale = "id-ID",
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined) return "";
  const format = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    ...options,
  });
  // Wire integers are strings; BigInt keeps every digit past 2^53.
  if (typeof value === "string")
    return /^-?\d+$/.test(value.trim())
      ? format.format(BigInt(value.trim()))
      : "";
  return Number.isFinite(value) ? format.format(value) : "";
}

/**
 * Format a MoneyIdr *string* (the wire money type) as currency (default IDR,
 * zero fractional digits). Money is a string end-to-end; this is the single
 * place it is parsed for display, and a non-finite / non-numeric value returns
 * "" so money never renders as "RpNaN"/"Rp∞".
 */
export function formatCurrency(
  value: string | null | undefined,
  locale = "id-ID",
  currency = "IDR",
  options: Intl.NumberFormatOptions = {},
): string {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(n);
}
